import {
  getUpdatedHotelPriceBreakdownPrices,
  getHotelBreakdownItems,
  useBalanceWithPoints,
} from "@apac/components/checkout";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Product } from "@b2bportal/purchase-api";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import { useApacHotelsStyles } from "../../hooks";
import { ApacHotelsComponent } from "../../types";
import defaultStyles from "./styles.module.scss";

export interface TotalAmountLinesProps {
  validateRoomPrice?: boolean;
  className?: string;
  hasFulfilled?: boolean;
  fiatFormatOptions?: Intl.NumberFormatOptions;
}

export const TotalAmountLines = ({
  className,
  validateRoomPrice = false,
  hasFulfilled = false,
  fiatFormatOptions,
}: TotalAmountLinesProps) => {
  const COMPONENT_KEY = ApacHotelsComponent.TotalAmountLines;
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();

  const { payNowTotal, payLaterBreakdown, tripTotal } =
    useCheckoutStateSelector(
      validateRoomPrice
        ? getUpdatedHotelPriceBreakdownPrices
        : getHotelBreakdownItems
    );

  const { remainingBalance } = useBalanceWithPoints(Product.Hotel);

  const renderLine = ({ value, label, type }) => (
    <div className={cn("item", [type])}>
      <div className={cn("item-label", [type])}>{label}</div>
      <div className={cn("item-value")}>{value}</div>
    </div>
  );

  return (
    <div className={clsx(block, className)}>
      {renderLine({
        label: t("total"),
        value: formatFiatCurrency(tripTotal?.fiat, fiatFormatOptions),
        type: "total",
      })}

      {renderLine({
        label: hasFulfilled
          ? t("apac.priceBreakdown.paidToday")
          : t("apac.priceBreakdown.dueToday"),
        value: formatFiatCurrency(payNowTotal?.fiat, fiatFormatOptions),
        type: "paynow",
      })}

      {payLaterBreakdown?.total != null &&
        renderLine({
          label: t("apac.priceBreakdown.dueAtCheckInLabel"),
          value: formatFiatCurrency(payLaterBreakdown.total, fiatFormatOptions),
          type: "paylater",
        })}

      {!hasFulfilled &&
        renderLine({
          label: t("amountDue"),
          value: formatFiatCurrency(remainingBalance, fiatFormatOptions),
          type: "amount-due",
        })}
    </div>
  );
};
